import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { postNamedCommand, postNamedQuery } from '../service'
import { useAppContext } from '../redux/slices/appContext'

dayjs.extend(utc)

const createUserMessage = (threadId, prompt, attachments = []) => ({
  threadId,
  threadMessageId: null,
  actor: 'user',
  message: prompt,
  data: {},
  isGenerating: false,
  generationStatus: null,
  responseId: null,
  attachments,
  isPlaceholder: true,
  createdDate: dayjs.utc().toISOString()
})

const createAgentPlaceholderMessage = (threadId, responseId) => ({
  threadId,
  threadMessageId: responseId,
  actor: 'assistan',
  message: '',
  data: {},
  isGenerating: true,
  generationStatus: 'Thinking...',
  responseId: null,
  attachments: [],
  isPlaceholder: true,
  createdDate: dayjs.utc().toISOString()
})

// List all user threads
export const useListAIUserThreads = (options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, categories } = options

  return useQuery({
    queryKey: ['denali/ai-user-threads', userId, categories],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'list-ai-user-threads', {
        categories: categories || ['assistant'] // Default to assistant category
      })
      return data
    },
    enabled
  })
}

// Get a specific thread
export const useAIThread = (threadId, options = {}) => {
  const { enabled = true, refetchInterval = null, includeMessages = true, includeAttachments = true } = options

  return useQuery({
    queryKey: ['denali/ai-thread', threadId, includeMessages, includeAttachments],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-thread', {
        threadId,
        categories: ['assistant'],
        includeMessages,
        includeAttachments
      })
      return data
    },
    enabled: enabled && !!threadId,
    refetchInterval,
    refetchOnWindowFocus: !!refetchInterval,
    staleTime: 0 // Allow using cached data but mark as stale so it can be updated
  })
}

// Get a specific thread
export const useRenameAiThread = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (scheduledPrompt) => {
      const { data } = await postNamedCommand('denali', 'ai-rename-thread', scheduledPrompt)

      return data
    },
    onSuccess: () => {
      // todo: make this modify the query cache instead of refetch
      queryClient.invalidateQueries({ queryKey: ['denali/ai-user-threads'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/search-ai-user-threads'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/ai-thread'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/search-ai-user-threads-infinite'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRemoveAiThread = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('denali', 'ai-remove-thread', command)

      return data
    },
    onSuccess: () => {
      // todo: make this modify the query cache instead of refetch
      queryClient.invalidateQueries({ queryKey: ['denali/ai-user-threads'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/search-ai-user-threads'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/ai-thread'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/search-ai-user-threads-infinite'], refetchType: 'all' }).catch(console.error)
    }
  })
}

// Get a specific thread message
export const useAIThreadMessage = (threadId, threadMessageId, options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/ai-thread-message', threadId, threadMessageId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-thread-message', {
        threadId,
        threadMessageId
      })
      return data
    },
    enabled: enabled && !!threadId && !!threadMessageId
  })
}

// Get a specific message by flexible parent type
export const useAiMessage = (parentInfo, messageId, options = {}) => {
  const { enabled = true } = options
  const parentKey = parentInfo ? JSON.stringify(parentInfo) : ''

  return useQuery({
    queryKey: ['denali/ai-message', parentKey, messageId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-message', {
        parentInfo,
        messageId
      })
      return data
    },
    enabled: enabled
  })
}

// Get message events by flexible parent type
export const useAiMessageEvents = (parentInfo, messageId, options = {}) => {
  const { enabled = true, refetchInterval } = options
  const parentKey = parentInfo ? JSON.stringify(parentInfo) : ''

  return useQuery({
    queryKey: ['denali/ai-message-events', parentKey, messageId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-message-events', {
        parentInfo,
        messageId
      })
      return data
    },
    enabled: enabled,
    refetchInterval
  })
}

// Get thread attachment
export const useAIThreadAttachment = (threadId, threadMessageAttachmentId, options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/ai-thread-attachment', threadId, threadMessageAttachmentId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-thread-attachment', {
        threadId,
        threadMessageAttachmentId
      })
      return data
    },
    enabled: enabled && !!threadId && !!threadMessageAttachmentId
  })
}

export const useAIToolCallDetails = (threadId, messageId, toolCallId, options = {}) => {
  const { enabled = true } = options
  return useQuery({
    queryKey: ['denali/ai-tool-call-details', threadId, messageId, toolCallId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-tool-call-details', {
        threadId,
        messageId,
        toolCallId
      })
      return data
    },
    enabled: enabled && !!threadId && !!messageId && !!toolCallId
  })
}

export const useListUserScheduledPrompts = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/list-user-scheduled-prompts'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'list-user-scheduled-prompts', {
      })
      return data
    },
    enabled: enabled
  })
}

export const useScheduledPromptById = (scheduleId, include, options = {}) => {
  const { enabled = true } = options
  const includeKey = include ? JSON.stringify(include) : null

  return useQuery({
    queryKey: ['denali/get-scheduled-prompt-by-id', scheduleId, includeKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-scheduled-prompt-by-id', {
        scheduleId,
        include
      })
      return data
    },
    enabled: enabled
  })
}

export const useCreateAiScheduledPrompt = () => {
  return useMutation({
    mutationFn: async (scheduledPrompt) => {
      const { data } = await postNamedCommand('denali', 'ai-create-scheduled-prompt', scheduledPrompt)

      return data
    }
  })
}

export const useModifyAiScheduledPrompt = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (scheduledPrompt) => {
      const { data } = await postNamedCommand('denali', 'ai-modify-scheduled-prompt', scheduledPrompt)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-scheduled-prompt-by-id'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/list-user-scheduled-prompts'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRemoveAiScheduledPrompt = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (scheduledPrompt) => {
      const { data } = await postNamedCommand('denali', 'ai-remove-scheduled-prompt', scheduledPrompt)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-scheduled-prompt-by-id'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/list-user-scheduled-prompts'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRemoveAiScheduledDigest = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (digest) => {
      const { data } = await postNamedCommand('denali', 'ai-remove-scheduled-digest', digest)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-scheduled-prompt-by-id'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/list-user-scheduled-prompts'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useCreateAiScheduledDigest = () => {
  return useMutation({
    mutationFn: async (scheduledDigest) => {
      const { data } = await postNamedCommand('denali', 'ai-create-scheduled-digest', scheduledDigest)

      return data
    }
  })
}

export const useModifyAiScheduledDigest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (scheduledDigest) => {
      const { data } = await postNamedCommand('denali', 'ai-modify-scheduled-digest', scheduledDigest)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-scheduled-prompt-by-id'], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['denali/list-user-scheduled-prompts'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useRunAiScheduledPrompt = () => {
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('denali', 'ai-run-scheduled-prompt', command)

      return data
    }
  })
}

export const useSearchAIUserThreads = (query = {}, options = {}) => {
  const { enabled = true } = options
  const queryKey = query ? JSON.stringify(query) : '{}'

  return useQuery({
    queryKey: ['denali/search-ai-user-threads', queryKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'search-ai-user-threads', query)
      return data
    },
    enabled: enabled
  })
}

export const useSearchAIUserThreadsInfinite = (query, options = {}) => {
  const { enabled = true, pageSize = 10 } = options
  const { searchString, orderBy } = query

  return useInfiniteQuery({
    queryKey: ['denali/search-ai-user-threads-infinite', searchString, orderBy],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await postNamedQuery('denali', 'search-ai-user-threads', {
        searchString,
        orderBy,
        skip: pageParam,
        take: pageSize
      })
      return data
    },
    enabled,
    getNextPageParam: (lastPage, allPages) => {
      // If the last page has fewer items than pageSize, we've reached the end
      if (!lastPage?.data || lastPage.data.length < pageSize) {
        return undefined
      }
      // Calculate next skip value
      return allPages.length * pageSize
    }
  })
}

export const useCountAiUserThreads = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/count-ai-user-threads'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'count-ai-user-threads', {})
      return data?.count
    },
    enabled: enabled
  })
}

export const useUserReadingList = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/get-user-reading-list'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-user-reading-list', {})
      return data
    },
    enabled: enabled
  })
}

// Invoke the Denali AI
export const useInvokeDenaliAssistant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ prompt, threadId, context, options, attachments }) => {
      if (threadId) {
        queryClient.setQueryData(['denali/ai-thread', threadId, true, true], prev => {
          if (!prev) {
            return {
              thread: {
                threadId,
                messages: [createUserMessage(threadId, prompt, attachments)]
              }
            }
          }
          return {
            ...prev,
            thread: {
              ...prev.thread,
              messages: [...prev.thread.messages, createUserMessage(threadId, prompt, attachments)]
            }
          }
        })
      }

      const request = {
        prompt,
        attachments,
        ...(threadId && { threadId }),
        ...(context && { context }),
        ...(options && { options })
      }

      const { data } = await postNamedCommand('denali', 'invoke-denali-assistant', request)

      return data
    },
    onSuccess: async (data, { threadId: originalThreadId, attachments: sentAttachments }) => {
      const threadId = data.threadId || originalThreadId
      const newMessage = data.message

      queryClient.setQueryData(['denali/ai-thread', threadId, true, true], prev => {
        if (!prev) {
          return {
            thread: {
              // Spread the full thread object from response (includes metadata, name, etc.)
              ...(data.thread || {}),
              threadId,
              messages: [{
                ...newMessage,
                attachments: newMessage.attachments || sentAttachments || []
              }, createAgentPlaceholderMessage(threadId, data.responseId)]
            }
          }
        }

        // Replace placeholder user message with actual message if it exists
        const messages = [...(prev.thread.messages || [])]
        const placeholderIndex = messages.findIndex(m => m.actor === 'user' && m.isPlaceholder)
        if (placeholderIndex !== -1) {
          const placeholderMessage = messages[placeholderIndex]
          messages[placeholderIndex] = {
            ...newMessage,
            attachments: newMessage.attachments || placeholderMessage.attachments || []
          }
        } else {
          messages.push({
            ...newMessage,
            attachments: newMessage.attachments || sentAttachments || []
          })
        }

        // Add agent placeholder message
        messages.push(createAgentPlaceholderMessage(threadId, data.responseId))

        return {
          ...prev,
          thread: {
            // Merge thread properties from response (including metadata)
            ...prev.thread,
            ...(data.thread || {}),
            // Ensure messages are updated
            messages
          }
        }
      })

      // Also invalidate the threads list to pick up the new thread
      queryClient.invalidateQueries(['denali/ai-user-threads'])
    }
  })
}

// Poll for message generation status
export const useMessagePolling = (message) => {
  const queryClient = useQueryClient()
  const refetchInterval = message?.isGenerating ? 2000 : null

  return useQuery({
    queryKey: ['denali/ai-thread-message-poll', message?.threadId, message?.threadMessageId],
    queryFn: async () => {
      try {
        const { data } = await postNamedQuery('denali', 'get-ai-thread-message', {
          threadId: message.threadId,
          threadMessageId: message.threadMessageId,
          categories: ['assistant'],
          includeAttachments: true
        })

        return data
      } catch (error) {
        // 204 or 404 means message is still being generated
        if (error.response?.status === 204 || error.response?.status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: !!message?.isGenerating && !!message?.threadId && !!message?.threadMessageId,
    refetchInterval,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (!data?.message) return

      const newMessage = data.message
      queryClient.setQueryData(['denali/ai-thread', message.threadId, true, true], prev => {
        if (!prev) return prev

        const messages = [...prev.thread.messages]
        const idx = messages.findIndex(m => m.threadMessageId === message.threadMessageId)
        if (idx > -1) {
          messages[idx] = newMessage
        }

        return {
          ...prev,
          thread: {
            ...prev.thread,
            messages
          }
        }
      })
    }
  })
}

// Note: Threads are created automatically when the first message is sent
// This is just a placeholder for potential future use
export const useCreateAIThread = () => {
  return {
    mutateAsync: async ({ title }) => {
      // Threads are created server-side when invoking the assistant
      // This is just a placeholder that returns a temporary ID
      return {
        threadId: null,
        title: title || 'New Chat',
        createdDate: dayjs.utc().toISOString()
      }
    }
  }
}

export const useGetAiUserPreferences = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/get-ai-user-preferences'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-user-preferences', {
        includeSchema: true
      })
      return data
    },
    enabled: enabled
  })
}

export const useModifyAiUserPreferences = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (changes) => {
      const { data } = await postNamedCommand('denali', 'modify-ai-user-preferences', { changes })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-ai-user-preferences'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useGetAiModels = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/get-ai-models'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-models', {
        includeProvider: true,
        includeTasks: true,
        hidden: false
      })
      return data
    },
    enabled: enabled
  })
}

export const useGetAiGuardrails = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/get-ai-guardrails'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-guardrails', {})
      return data
    },
    enabled: enabled
  })
}

export const useListAddressableInternalUsers = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/list-addressable-internal-users'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'list-addressable-internal-users', {})

      return data
    },
    enabled
  })
}

export const useCopyScheduledPrompt = () => {
  return useMutation({
    mutationFn: async ({ scheduleId, userIds }) => {
      const { data } = await postNamedCommand('denali', 'ai-copy-scheduled-prompt', {
        scheduleId,
        userIds
      })

      return data
    }
  })
}

export const useGetAiUsage = (options = {}) => {
  const { enabled = true } = options

  return useQuery({
    queryKey: ['denali/get-ai-usage'],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-usage', {})
      return data
    },
    enabled: enabled
  })
}

// Provide feedback on a message
export const useProvideFeedback = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ threadMessageId, type, feedbackText, threadId }) => {
      const command = {
        threadMessageId,
        type
      }
      if (feedbackText) {
        command.feedbackText = feedbackText
      }
      const { data } = await postNamedCommand('denali', 'ai-provide-feedback', command)
      return { ...data, threadMessageId, type, feedbackText, threadId }
    },
    onSuccess: (data) => {
      if (data.threadId) {
        queryClient.setQueryData(['denali/ai-thread', data.threadId, true, true], prev => {
          if (!prev) return prev

          const messages = [...(prev.thread.messages || [])]
          const messageIndex = messages.findIndex(m => m.threadMessageId === data.threadMessageId)

          if (messageIndex > -1) {
            messages[messageIndex] = {
              ...messages[messageIndex],
              feedback: {
                type: data.type,
                ...(data.feedbackText && { feedbackText: data.feedbackText })
              }
            }
          }

          return {
            ...prev,
            thread: {
              ...prev.thread,
              messages
            }
          }
        })
      }
    }
  })
}

// Upload attachment - gets presigned URL and uploads file to S3
export const useUploadAttachment = () => {
  return useMutation({
    mutationFn: async ({ fileName, contentType }) => {
      const { data } = await postNamedCommand('denali', 'ai-upload-attachment', {
        fileName,
        contentType
      })
      return data
    }
  })
}

// Retry a message - deletes the message and all after it, then reprocesses the last user message
export const useRetryMessage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ threadId, threadMessageId }) => {
      const { data } = await postNamedCommand('denali', 'retry-denali-assistant', {
        threadId,
        threadMessageId
      })
      return data
    },
    onSuccess: async (data, { threadId, threadMessageId }) => {
      // If the backend returns the updated thread directly, use it
      if (data?.thread) {
        queryClient.setQueryData(['denali/ai-thread', threadId, true, true], data)
        return
      }

      // If responseId is returned, optimistically add a placeholder message to start polling
      if (data?.responseId) {
        const placeholder = createAgentPlaceholderMessage(threadId, data.responseId)

        queryClient.setQueryData(['denali/ai-thread', threadId, true, true], prev => {
          if (!prev) {
            return {
              thread: {
                threadId,
                messages: [placeholder]
              }
            }
          }

          const messages = [...(prev.thread.messages || [])]
          const retryIndex = messages.findIndex(m => m.threadMessageId === threadMessageId)

          if (retryIndex !== -1) {
            messages.splice(retryIndex)
          }

          messages.push(placeholder)

          return {
            ...prev,
            thread: {
              ...prev.thread,
              messages
            }
          }
        })
        return
      }

      await queryClient.invalidateQueries({ queryKey: ['denali/ai-thread', threadId, true, true] })
    }
  })
}

export const useGetAiSkills = (options = {}) => {
  const { enabled = true, skillIds, allVersions = false, skip = 0, take = 1000 } = options
  const queryKey = skillIds ? JSON.stringify(skillIds.sort()) : null

  return useQuery({
    queryKey: ['denali/get-ai-skills', queryKey, allVersions, skip, take],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-ai-skills', {
        ...(skillIds && { skillIds }),
        allVersions,
        skip,
        take
      })
      return data
    },
    enabled
  })
}

export const useInsertAiSkill = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ skillId, skillName, skillContent }) => {
      const { data } = await postNamedCommand('denali', 'ai-insert-skill', {
        skillId,
        skillName,
        skillContent
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['denali/get-ai-skills'], refetchType: 'all' }).catch(console.error)
    }
  })
}
